import { useSyncReportUrl } from '@src/packages/dashboard/hooks/useSyncReportUrl';
import { useReportUrlFilters } from '@src/packages/dashboard/hooks/useReportUrlFilters';
import { selectCalendarTimeZone, getCalendarRange } from '@src/packages/utils/dates/calendar';
import { DateUtil } from '@src/packages/utils/dates';
/* eslint-disable react-hooks/exhaustive-deps */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { restoreClearedReportFilters } from '@src/packages/dashboard/utils/restoreClearedReportFilters';

import { auth } from '@src/firebase/firebase';

import { SCHEDULE_TYPES } from '@src/constants/commons';
import { camelize, getRange } from '@src/packages/dashboard/utils';
import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';

import {
  getRouteToDashboardSchedules,
  getRouteToEventTypesDrawer,
  getRouteToTimelineEventDrawer,
} from '@src/packages/dashboard/constants';
import { useCostCenterFilterIds } from '@src/packages/costCenters/hooks/useCostCenterFilterIds';
import { ALLOWED_ROLES, getFullUrl, useElasticDivisionScope } from '@src/packages/utils';
import useGetDivisionsAllowed from '@src/packages/utils/hooks/useGetDivisionsAllowed';
import { disableCalendarDatesAfterToday } from '@src/packages/utils/dates';
import useGetParams from '@src/packages/utils/hooks/useGetParams';
import { get as getLocal, remove as removeLocal } from '@src/packages/utils/storage';
import { get, set } from '@src/packages/utils/sessionStorage';
import { useDivisionNames } from '@src/hooks/useDivisionNames';
import type { CommonReduxState } from '@src/models/commons/reduxState.interface';

import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { calculateScheduleProgress } from '../constants';
import {
  getDashboardScheduleDivisionIds,
  getDashboardSchedulesById,
  getDashboardSchedulesWithDivisionNames,
} from '../../utils/getDashboardSchedulesById';
import type { SetValueProps } from '@src/components/FilterDropDown/FilterDropDown.interfaces';

import {
  buildDefaultScheduleChartFilters,
  buildInitScheduleFilterValues,
  buildScheduleFilterSetValueProps,
  areScheduleSelectionsEqual,
  filterValuesToScheduleFilters,
  filterValuesToScheduleFiltersUi,
  mergeFilterSetValueProps,
  normalizeStoredScheduleFilters,
  SCHEDULES_REPORT_MAX_SPAN,
  SCHEDULES_DASHBOARD_FILTER_FIELDS,
  SCHEDULES_DASHBOARD_FILTERS_STORAGE_KEY,
  scheduleFiltersToStoragePayload,
  type ScheduleChartFilters,
} from '../schedulesFilterConfig';
import type {
  GoToTimelineParams,
  OnOpenEventTypesDrawerParams,
  ScheduleDashboardContextValue,
  ScheduleDashboardDrawerState,
  ScheduleDashboardDrawerType,
  SchedulesDashboardReduxState,
  SelectedSchedulesState,
  SelectedUsersState,
  TableDataByDay,
  TimeAggregationBucket,
} from '../types';

const EMPTY_ASSIGNED_DIVISIONS: CommonReduxState['divisions']['assigned']['data']['divisions'] = {};

export const ScheduleDashboardContext = createContext<ScheduleDashboardContextValue | null>(null);

interface ScheduleDashboardProviderProps {
  children: ReactNode;
}

export const ScheduleDashboardProvider = ({ children }: ScheduleDashboardProviderProps) => {
  const intl = useIntl();
  const organizationTimeZone = useSelector(selectCalendarTimeZone);
  const { values: filters, setValue: setFilters, toggle, isOpen } = useSecondaryPanel();

  const urlFilters = useReportUrlFilters('schedules');

  const initialChartFilters = useMemo(() => {
    const restoredUrl = normalizeStoredScheduleFilters(urlFilters, organizationTimeZone);
    if (restoredUrl) return restoredUrl;
    const sessionValue = get(SCHEDULES_DASHBOARD_FILTERS_STORAGE_KEY);
    if (sessionValue) return normalizeStoredScheduleFilters(sessionValue, organizationTimeZone) ?? buildDefaultScheduleChartFilters(organizationTimeZone);

    // Keep the existing preference once when upgrading from the previous localStorage behavior.
    const legacyLocalValue = getLocal(SCHEDULES_DASHBOARD_FILTERS_STORAGE_KEY);
    if (legacyLocalValue) {
      set(SCHEDULES_DASHBOARD_FILTERS_STORAGE_KEY, legacyLocalValue);
      removeLocal(SCHEDULES_DASHBOARD_FILTERS_STORAGE_KEY);
    }
    return normalizeStoredScheduleFilters(legacyLocalValue, organizationTimeZone) ?? buildDefaultScheduleChartFilters(organizationTimeZone);
  }, []);

  useEffect(() => {
    if (urlFilters) set(SCHEDULES_DASHBOARD_FILTERS_STORAGE_KEY, scheduleFiltersToStoragePayload(initialChartFilters));
  }, [initialChartFilters, urlFilters]);

  const initialFiltersRef = useRef(buildInitScheduleFilterValues(initialChartFilters, intl));

  /**
   * The panel seeds `filters` with `initialFilters` in a mount effect, so the first render (and the
   * initial fetch that runs in the same commit) still sees an empty map — or, when coming back from
   * another screen, that screen's leftover values. Falling back to the persisted filters until the
   * values are ours keeps the query aligned with what the panel is about to show.
   */
  const draftChartFilters = useMemo(
    () => filterValuesToScheduleFiltersUi(filters, initialChartFilters, organizationTimeZone),
    [filters, initialChartFilters, organizationTimeZone],
  );
  const [chartFilters, setChartFilters] = useState(initialChartFilters);
  const hasPendingFilters =
    JSON.stringify(scheduleFiltersToStoragePayload(draftChartFilters)) !==
    JSON.stringify(scheduleFiltersToStoragePayload(chartFilters));

  const { relative, dateRange, groupBy, schedules: selectedSchedules, costCenterIds } =
    chartFilters;
  const { isRelative, relativeAmount, dateType } = relative;
  const {
    isAllowed: costCentersAllowed,
    queryIds: costCenterQueryIds,
    isExpandReady: costCenterExpandReady,
    listLoaded: costCenterListLoaded,
    expandSelection: expandCostCenterSelection,
  } = useCostCenterFilterIds(costCenterIds);

  const [aggregationTimeZone, setAggregationTimeZone] = useState('UTC');
  const aggregationTimeZoneRef = useRef<string | null>(null);
  const [aggregations, setAggregations] = useState<TimeAggregationBucket[]>([]);
  const [schedulesById, setSchedulesById] = useState<Record<string, SelectedSchedulesState['byId'][string]>>({});
  const [loading, setLoading] = useState(false);
  const [scheduleSelectorLoading, setScheduleSelectorLoading] = useState(false);
  const [showEventTypeDrawer, setShowEventTypeDrawer] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUsersState | null>(null);
  const [drawerState, setDrawerState] = useState<ScheduleDashboardDrawerState>({
    users: false,
  });

  const selectedDivs = useSelector(
    ({ divisions }: SchedulesDashboardReduxState) => divisions.selector.ids || [],
  );
  const reportDivisions = useGetDivisionsAllowed(ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.REPORTS.LIST);
  const { divisionIds: queryDivisionIds, canQuery: canQueryDivisions } =
    useElasticDivisionScope(reportDivisions);
  const { permissionsLoaded } = reportDivisions;
  useSyncReportUrl(chartFilters, 'schedules', selectedDivs);

  const orgId = useSelector(
    (state: SchedulesDashboardReduxState) => state.organizations.organization.id,
  );

  const assignedDivisions = useSelector(
    (state: CommonReduxState) =>
      state.divisions.assigned.data?.divisions ?? EMPTY_ASSIGNED_DIVISIONS,
  );
  const q = useGetParams();
  const history = useHistory<{ shadow?: boolean }>();
  const { url } = useRouteMatch();
  const { location: { state: { shadow = false } = {} } = {} } = history || {};

  const divId = q.get('divId');
  const triggerId = q.get('triggerId')?.replace('/events', '');
  const dateKey = q.get('dateKey');
  const showEventTypes = !!dateKey?.includes('/event_types');
  const openTimelineEvents = !!divId && !!triggerId;

  const showEventTypesRef = useRef(showEventTypes);
  showEventTypesRef.current = showEventTypes;

  const selectedSchedulesRef = useRef(draftChartFilters.schedules);

  const intlRef = useRef(intl);
  intlRef.current = intl;

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const initialFetchKeyRef = useRef<string | null>(null);
  const requestSequenceRef = useRef(0);

  const fetchParamsRef = useRef({
    orgId,
    organizationTimeZone,
    selectedDivs,
    queryDivisionIds,
    canQueryDivisions,
    relative,
    dateRange,
    dateType,
    isRelative,
    relativeAmount,
    groupBy,
    selectedSchedules,
    costCenterIds,
    costCenterQueryIds,
    costCentersAllowed,
    costCenterExpandReady,
    costCenterListLoaded,
    expandCostCenterSelection,
  });

  fetchParamsRef.current = {
    orgId,
    organizationTimeZone,
    selectedDivs,
    queryDivisionIds,
    canQueryDivisions,
    relative,
    dateRange,
    dateType,
    isRelative,
    relativeAmount,
    groupBy,
    selectedSchedules,
    costCenterIds,
    costCenterQueryIds,
    costCentersAllowed,
    costCenterExpandReady,
    costCenterListLoaded,
    expandCostCenterSelection,
  };
  const chartFiltersRef = useRef(chartFilters);
  chartFiltersRef.current = chartFilters;

  const executeFetch = useCallback(
    (
      schedulesOverride?: SelectedSchedulesState,
      {
        requireSelectedIds = false,
        chartFiltersOverride,
      }: {
        requireSelectedIds?: boolean;
        chartFiltersOverride?: ScheduleChartFilters;
      } = {},
    ) => {
      const requestSequence = ++requestSequenceRef.current;
      const {
        orgId: currentOrgId,
        selectedDivs: currentSelectedDivs,
        queryDivisionIds: currentQueryDivisionIds,
        canQueryDivisions: currentCanQueryDivisions,
      } = fetchParamsRef.current;
      const chart = chartFiltersOverride ?? {
        relative: fetchParamsRef.current.relative,
        dateRange: fetchParamsRef.current.dateRange,
        groupBy: fetchParamsRef.current.groupBy,
        schedules: schedulesOverride ?? fetchParamsRef.current.selectedSchedules,
        costCenterIds: fetchParamsRef.current.costCenterIds,
      };
      const schedules = schedulesOverride ?? chart.schedules;
      const { relative: nextRelative, dateRange: nextDateRange, groupBy: nextGroupBy } = chart;
      const {
        dateType: nextDateType,
        isRelative: nextIsRelative,
        relativeAmount: nextRelativeAmount,
      } = nextRelative;

      if (showEventTypesRef.current) {
        return;
      }

      if (requireSelectedIds && Object.keys(schedules.byId).length === 0) {
        setAggregations([]);
        setLoading(false);
        return;
      }

      if (!currentOrgId || !currentSelectedDivs.length || !currentCanQueryDivisions) {
        setAggregations([]);
        setLoading(false);
        return;
      }

      if (
        fetchParamsRef.current.costCentersAllowed
        && chart.costCenterIds?.length
        && !fetchParamsRef.current.costCenterListLoaded
      ) {
        return;
      }

      const range = getRange(nextDateRange, nextDateType, nextIsRelative, nextRelativeAmount, SCHEDULES_REPORT_MAX_SPAN, fetchParamsRef.current.organizationTimeZone, true);
      const nextCostCenterIds = fetchParamsRef.current.expandCostCenterSelection(
        chart.costCenterIds,
      );
      setLoading(true);
      apiCallable(API_OPERATIONS.SCHEDULE_DASHBOARD)({
        organizationId: currentOrgId,
        divisionIds: currentQueryDivisionIds,
        uid: auth.currentUser?.uid,
        range,
        calendarRange: getCalendarRange(range, fetchParamsRef.current.organizationTimeZone),
        interval: nextGroupBy,
        scheduleIds: schedules.selectedAll ? [] : Object.keys(schedules.byId),
        type: SCHEDULE_TYPES.schedule,
        ...(nextCostCenterIds?.length ? { costCenterIds: nextCostCenterIds } : {}),
      })
        .then(result => {
          if (requestSequence !== requestSequenceRef.current) return;

          const schedulesById = getDashboardSchedulesById(result);
          const { data } = camelize(result) as {
            data: {
              calendarTimeZone?: string;
              aggregations: { timeRange: { histogram: { buckets: TimeAggregationBucket[] } } };
            };
          };
          aggregationTimeZoneRef.current = data.calendarTimeZone || null;
          setAggregationTimeZone(aggregationTimeZoneRef.current || 'UTC');
          setAggregations(data.aggregations.timeRange.histogram.buckets);
          setSchedulesById(schedulesById);
          setLoading(false);
        })
        .catch(error => {
          if (requestSequence !== requestSequenceRef.current) return;

          console.error('🔍 [SchedulesDashboard] API Error:', error);
          setAggregations([]);
          setSchedulesById({});
          setLoading(false);
        });
    },
    [],
  );

  const historyRef = useRef(history);
  historyRef.current = history;

  const urlRef = useRef(url);
  urlRef.current = url;

  const toggleDrawer = useCallback((type: ScheduleDashboardDrawerType) => {
    setDrawerState(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  const runQuery = useCallback(() => {
    executeFetch();
  }, [executeFetch]);

  // Only when expansion becomes ready for a pending selection (avoids duplicate apply fetches).
  useEffect(() => {
    if (!costCentersAllowed || !costCenterExpandReady || !costCenterIds.length) return;
    executeFetch(undefined, { chartFiltersOverride: chartFiltersRef.current });
    // Intentionally keyed on readiness, not queryIds — applyFilters already fetches when ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterExpandReady]);

  /**
   * Accepts several field updates at once so filters that change together (relative mode + date
   * range) reach the API as a single query. `filtersRef` is advanced synchronously because the
   * panel state lands on the next render, and until then it would still hold the previous values.
   */
  const applyFilter = useCallback(
    (props: SetValueProps | SetValueProps[]) => {
      const propsList = restoreClearedReportFilters(
        props,
        SCHEDULES_DASHBOARD_FILTER_FIELDS,
        () => buildInitScheduleFilterValues(buildDefaultScheduleChartFilters(organizationTimeZone), intlRef.current),
      );
      if (!propsList.length) return;

      const baseFilters = Object.keys(filtersRef.current).length
        ? filtersRef.current
        : initialFiltersRef.current;
      const nextFilters = propsList.reduce(
        (acc, current) => mergeFilterSetValueProps(acc, current),
        baseFilters,
      );
      selectedSchedulesRef.current = filterValuesToScheduleFilters(nextFilters, organizationTimeZone).schedules;
      filtersRef.current = nextFilters;
      propsList.forEach(item => setFilters(item));
    },
    [organizationTimeZone, setFilters],
  );

  const applyFilters = useCallback(() => {
    const baseFilters = Object.keys(filtersRef.current).length
      ? filtersRef.current
      : initialFiltersRef.current;
    const nextChartFilters = {
      ...filterValuesToScheduleFilters(baseFilters, organizationTimeZone),
      schedules: selectedSchedulesRef.current,
    };
    setChartFilters(nextChartFilters);
    if (!showEventTypesRef.current) {
      executeFetch(undefined, {
        chartFiltersOverride: nextChartFilters,
        requireSelectedIds: !nextChartFilters.schedules.selectedAll,
      });
    }
  }, [executeFetch, organizationTimeZone]);

  const setSelectedSchedules = useCallback(
    (next: SelectedSchedulesState | ((prev: SelectedSchedulesState) => SelectedSchedulesState)) => {
      const currentSelection = selectedSchedulesRef.current;
      const resolved = typeof next === 'function' ? next(currentSelection) : next;

      if (areScheduleSelectionsEqual(currentSelection, resolved)) {
        return;
      }
      selectedSchedulesRef.current = resolved;

      const schedulesProps = buildScheduleFilterSetValueProps(
        SCHEDULES_DASHBOARD_FILTER_FIELDS.SCHEDULES,
        resolved,
        intlRef.current,
      );

      filtersRef.current = mergeFilterSetValueProps(filtersRef.current, schedulesProps);
      setFilters(schedulesProps);
    },
    [setFilters],
  );

  const onOpenEventTypesDrawer = useCallback(
    ({ schedule = {}, dateKey: dateKeyArg, groupByArg }: OnOpenEventTypesDrawerParams) => {
      const {
        dateRange: currentDateRange,
        dateType: currentDateType,
        isRelative: currentIsRelative,
        relativeAmount: currentRelativeAmount,
        groupBy: currentGroupBy,
      } = fetchParamsRef.current;

      const range = getRange(
        currentDateRange,
        currentDateType,
        currentIsRelative,
        currentRelativeAmount,
        SCHEDULES_REPORT_MAX_SPAN,
        fetchParamsRef.current.organizationTimeZone,
        true,
      );
      let path = getFullUrl(
        getRouteToEventTypesDrawer(
          schedule.divId,
          range.gte,
          range.lte,
          currentGroupBy,
          schedule.id,
          dateKeyArg,
          groupByArg,
        ),
        urlRef.current,
      );
      if (aggregationTimeZoneRef.current) path += `&calendarTimeZone=${encodeURIComponent(aggregationTimeZoneRef.current)}`;
      historyRef.current.push(path, { shadow: true });
      setShowEventTypeDrawer(true);
    },
    [],
  );

  const goToTimeline = useCallback(
    ({
      divisionId,
      scheduleId,
      triggerID,
      dateKeyArg,
      groupByArg,
      finishedExecutions,
      expectedExecutions,
    }: GoToTimelineParams) => {
      const {
        orgId: currentOrgId,
        dateRange: currentDateRange,
        dateType: currentDateType,
        isRelative: currentIsRelative,
        relativeAmount: currentRelativeAmount,
      } = fetchParamsRef.current;

      const range = getRange(
        currentDateRange,
        currentDateType,
        currentIsRelative,
        currentRelativeAmount,
        SCHEDULES_REPORT_MAX_SPAN,
        fetchParamsRef.current.organizationTimeZone,
        true,
      );
      let path = getFullUrl(
        getRouteToTimelineEventDrawer(
          currentOrgId,
          divisionId,
          triggerID,
          scheduleId,
          range.gte,
          range.lte,
          dateKeyArg,
          groupByArg,
        ),
        urlRef.current,
      );

      if (finishedExecutions != null && expectedExecutions != null) {
        const separator = path.includes('?') ? '&' : '?';
        path = `${path}${separator}finishedExecutions=${finishedExecutions}&expectedExecutions=${expectedExecutions}`;
      }

      if (aggregationTimeZoneRef.current) path += `&calendarTimeZone=${encodeURIComponent(aggregationTimeZoneRef.current)}`;
      historyRef.current.push(path, { shadow: true });
    },
    [],
  );

  const onBackFromTimeline = useCallback(() => {
    if (shadow) history.goBack();
    else history.push(getFullUrl(getRouteToDashboardSchedules(), url));
  }, [shadow, history, url]);

  const mainLoading = loading || scheduleSelectorLoading;

  const divisionNameSourceSchedulesById = useMemo(
    () => ({ ...selectedSchedules.byId, ...schedulesById }),
    [selectedSchedules.byId, schedulesById],
  );
  const dashboardDivisionIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...getDashboardScheduleDivisionIds(divisionNameSourceSchedulesById),
          ...queryDivisionIds,
        ]),
      ),
    [divisionNameSourceSchedulesById, queryDivisionIds],
  );
  const { getDivisionName } = useDivisionNames({
    organizationId: orgId,
    divisionIds: dashboardDivisionIds,
    assignedDivisions,
  });
  const schedulesByIdWithDivisionNames = useMemo(
    () =>
      getDashboardSchedulesWithDivisionNames(
        divisionNameSourceSchedulesById,
        getDivisionName,
      ),
    [divisionNameSourceSchedulesById, getDivisionName],
  );

  const tableDataByDay = useMemo<TableDataByDay[]>(() => {
    return aggregations
      .slice()
      .reverse()
      .map(day => {
        const hasSchedule = !!day.bySchedule.buckets.length;
        if (!hasSchedule) return null;

        const dataSource = day.bySchedule.buckets
          .map(schAggregation => {
            // The API already applies the selection; the selector may not have loaded its page yet.
            const selectedSchedule = selectedSchedules.byId[schAggregation.key];
            const sch = schedulesByIdWithDivisionNames[schAggregation.key] ?? selectedSchedule ?? {
              id: schAggregation.key,
              ...(queryDivisionIds.length === 1
                ? {
                    divId: queryDivisionIds[0],
                    divisionName: getDivisionName(queryDivisionIds[0]),
                  }
                : {}),
            };

            const scheduleData = calculateScheduleProgress(schAggregation);

            return {
              id: `${day.key}-${schAggregation.key}`,
              dateKey: day.key,
              schedule: sch,
              aggregation: schAggregation,
              scheduleData,
            };
          })
          .filter((row): row is NonNullable<typeof row> => row !== null);

        return {
          day: Number.isFinite(Number(day.key)) ? DateUtil.toDateString(Number(day.key), 'YYYY-MM-DD', aggregationTimeZone) : day.key,
          dataSource,
        };
      })
      .filter((dayData): dayData is TableDataByDay => dayData !== null);
  }, [aggregationTimeZone, aggregations, schedulesByIdWithDivisionNames, selectedSchedules.byId, queryDivisionIds, getDivisionName]);

  useEffect(() => {
    set(SCHEDULES_DASHBOARD_FILTERS_STORAGE_KEY, scheduleFiltersToStoragePayload(draftChartFilters));
  }, [draftChartFilters]);

  const selectedDivsKey = [...selectedDivs].sort().join(',');
  const queryDivisionIdsKey = queryDivisionIds.join(',');

  useEffect(() => {
    if (showEventTypesRef.current) {
      return;
    }

    if (!orgId || !selectedDivsKey || !canQueryDivisions) {
      return;
    }

    // `queryDivisionIds` can collapse to `[]` (org-wide "all divisions") after the
    // selector ids are already set. Key on both so that collapse refetches.
    const fetchKey = `${orgId}:${selectedDivsKey}:${queryDivisionIdsKey}:${organizationTimeZone}`;
    if (initialFetchKeyRef.current === fetchKey) {
      return;
    }

    initialFetchKeyRef.current = fetchKey;
    // The panel is shared across screens and only seeds `filters` with ours in a mount effect, so
    // during this commit `filters` is still empty or holds the previous screen's values. Querying
    // the restored filters explicitly keeps the buckets aligned with the groupBy the panel is
    // about to show — otherwise a foreign `groupBy` labels, say, daily buckets as whole months.
    executeFetch(undefined, { chartFiltersOverride: chartFiltersRef.current });
  }, [canQueryDivisions, executeFetch, initialChartFilters, orgId, organizationTimeZone, queryDivisionIdsKey, selectedDivsKey]);

  const value = useMemo<ScheduleDashboardContextValue>(
    () => ({
      chartFilters,
      draftChartFilters,
      hasPendingFilters,
      filters,
      initialFilters: initialFiltersRef.current,
      isRelative,
      relativeAmount,
      dateType,
      dateRange,
      selectedSchedules,
      loading,
      scheduleSelectorLoading,
      showEventTypeDrawer,
      mainLoading,
      groupBy,
      tableDataByDay,
      selectedUsers,
      drawerState,
      queryDivisionIds,
      canQueryDivisions,
      permissionsLoaded,
      orgId,
      divId,
      triggerId,
      dateKey,
      showEventTypes,
      openTimelineEvents,
      disabledDate: disableCalendarDatesAfterToday,
      intl,
      url,
      setFilters: applyFilter,
      applyFilters,
      setSelectedSchedules,
      setScheduleSelectorLoading,
      setShowEventTypeDrawer,
      setSelectedUsers,
      fetch: runQuery,
      onOpenEventTypesDrawer,
      goToTimeline,
      onBackFromTimeline,
      toggleDrawer,
      toggle,
      isOpen,
    }),
    [
      chartFilters,
      draftChartFilters,
      hasPendingFilters,
      filters,
      isRelative,
      relativeAmount,
      dateType,
      dateRange,
      selectedSchedules,
      loading,
      scheduleSelectorLoading,
      showEventTypeDrawer,
      mainLoading,
      groupBy,
      tableDataByDay,
      selectedUsers,
      drawerState,
      queryDivisionIds,
      canQueryDivisions,
      permissionsLoaded,
      orgId,
      divId,
      triggerId,
      dateKey,
      showEventTypes,
      openTimelineEvents,
      intl,
      url,
      applyFilter,
      applyFilters,
      setSelectedSchedules,
      runQuery,
      onOpenEventTypesDrawer,
      goToTimeline,
      onBackFromTimeline,
      toggleDrawer,
      toggle,
      isOpen,
    ],
  );

  return (
    <ScheduleDashboardContext.Provider value={value}>{children}</ScheduleDashboardContext.Provider>
  );
};

export const useScheduleDashboardContext = (): ScheduleDashboardContextValue => {
  const context = useContext(ScheduleDashboardContext);
  if (!context) {
    throw new Error('useScheduleDashboardContext must be used within a ScheduleDashboardProvider');
  }
  return context;
};
